import type { ConsentState } from './types';

/** Bump when the consent schema or policy meaning changes — older stored
 *  consent is then treated as undecided and the user is re-prompted. */
export const CONSENT_VERSION = 2;

const STORAGE_KEY = 'stitchpress-consent';

/** Opt-in default: nothing granted, nothing decided. */
export const DEFAULT_CONSENT: ConsentState = {
    analytics: false,
    sessionReplay: false,
    errorMonitoring: false,
    decided: false,
};

function isBool(v: unknown): v is boolean {
    return typeof v === 'boolean';
}

/**
 * Read persisted consent. Never throws: missing, corrupt, or version-mismatched
 * storage all collapse to DEFAULT_CONSENT (which re-shows the banner).
 */
export function readConsent(): ConsentState {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return DEFAULT_CONSENT;
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        if (parsed.version !== CONSENT_VERSION) return DEFAULT_CONSENT;
        if (
            !isBool(parsed.analytics) ||
            !isBool(parsed.sessionReplay) ||
            !isBool(parsed.errorMonitoring) ||
            !isBool(parsed.decided)
        ) {
            return DEFAULT_CONSENT;
        }
        return {
            analytics: parsed.analytics,
            sessionReplay: parsed.sessionReplay,
            errorMonitoring: parsed.errorMonitoring,
            decided: parsed.decided,
        };
    } catch {
        return DEFAULT_CONSENT;
    }
}

/** Persist consent with the current schema version. Swallows storage errors. */
export function writeConsent(state: ConsentState): void {
    try {
        window.localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ version: CONSENT_VERSION, ...state }),
        );
    } catch {
        // Ignore storage failures (private mode, quota, etc.).
    }
}

/** Whether the Statsig client should be initialized at all. */
export function isAnalyticsActive(state: ConsentState): boolean {
    return state.analytics === true;
}

/** Whether Sentry (error monitoring, tracing, profiling, logging) may initialize. */
export function isErrorMonitoringActive(state: ConsentState): boolean {
    return state.errorMonitoring === true;
}
